import { useAppRouteState } from "@/app/app/_/routing";
import { useBackendQuery as useQuery } from "@/hooks/useBackendQuery";
import { Queries } from "@/constants";
import { idToIdentifier } from "@/objects/@utils";
import { State } from "@/state";
import { useCallback, useEffect, useState } from "react";
import { useRuleEditorStore } from "./ruleEditorStore";
import type { RuleFragment } from "@/__generated__/graphql";

function extractRuleFragment(queryNode: unknown): RuleFragment | null {
  if (
    !queryNode ||
    typeof queryNode !== "object" ||
    (queryNode as { __typename?: string }).__typename !== "RuleDefinitionObject"
  )
    return null;
  return queryNode as RuleFragment;
}

/**
 * Hydrates ``useRuleEditorStore`` from GraphQL for the linear Rules editor:
 * the rule *ruleId* names, or - on the rules page - the one it has open.
 */
export const useRuleEditorState = (ruleId?: string) => {
  const routeState = useAppRouteState();
  const activeTab = State((state) => state.activeTab);
  const openRuleId = State(
    (state) =>
      (state.activeTabNodeIds as any)?.rules?.[activeTab] ?? routeState.ruleId,
  );
  const ruleNodeId = ruleId ?? openRuleId;

  const { data } = useQuery(Queries.RULE_QUERY, {
    variables: { id: ruleNodeId },
    skip: !ruleNodeId,
  });

  const queryNode = (data as any)?.node;

  const [ruleLocal, setRuleLocal] = useState<RuleFragment | null>(null);

  const _setActions = useRuleEditorStore((s) => s._setActions);
  const reset = useRuleEditorStore((s) => s.reset);

  useEffect(() => {
    const serverRule = extractRuleFragment(queryNode);
    if (!serverRule) {
      setRuleLocal(null);
      return;
    }
    setRuleLocal((prev) => {
      if (!prev || prev.id !== serverRule.id) return serverRule;

      const serverSuggestionIds = new Set(
        serverRule.suggestions
          .map((s) => idToIdentifier(s.id) ?? s.id)
          .filter(Boolean),
      );
      const tempLocalSuggestions = prev.suggestions.filter((s) => {
        const ident = idToIdentifier(s.id);
        return ident === null && !serverSuggestionIds.has(s.id);
      });

      return {
        ...serverRule,
        suggestions: [...serverRule.suggestions, ...tempLocalSuggestions],
      };
    });
  }, [queryNode]);

  const updateRule = useCallback(
    (updater: (r: RuleFragment) => RuleFragment) => {
      setRuleLocal((prev) => {
        if (!prev) return prev;
        const next = updater(prev);
        useRuleEditorStore.setState({ rule: next });
        return next;
      });
    },
    [],
  );

  const setSelectedNodeId = useCallback((id: string | null) => {
    const suggestion = useRuleEditorStore
      .getState()
      .rule?.suggestions.find((s) => s.id === id);
    useRuleEditorStore.setState({
      selectedNodeId: id,
      selectedNodeTitle: suggestion
        ? (suggestion.__typename
            ?.replace("RuleDefinitionSuggestion", "")
            .replace("Object", "") ?? null)
        : null,
      selectedNodeType: suggestion?.__typename ?? null,
    });
  }, []);

  const setRuleAction = useCallback((rule: RuleFragment | null) => {
    setRuleLocal(rule);
    useRuleEditorStore.setState({ rule });
  }, []);

  useEffect(() => {
    _setActions({
      setRule: setRuleAction,
      updateRule,
      setSelectedNodeId,
      setNodes: () => {},
      setEdges: () => {},
      setAddEdgeTarget: () => {},
      updateSelectedNodeData: () => {},
      updateNodeData: () => {},
      addEdgeFromPanel: () => {},
      removeEdge: () => {},
      updateEdgeLabel: () => {},
      createNode: () => {},
      organizeNodes: () => {},
      setIsLayoutAnimating: () => {},
    });
  }, [_setActions, setSelectedNodeId, setRuleAction, updateRule]);

  useEffect(() => {
    useRuleEditorStore.setState({ rule: ruleLocal });
  }, [ruleLocal]);

  useEffect(() => {
    useRuleEditorStore.setState({
      nodes: [],
      edges: [],
      serverNodesById: {},
    });
  }, [ruleLocal]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);
};
